import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import BrandMark from '../BrandMark';
import { publicApi } from './api';
import './BuzzerSolo.css';

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24);
}

export default function BuzzerSoloSignupPage({ onLogin }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    businessName: '',
    companySlug: '',
    taxId: '',
    address: '',
    ownerName: '',
    username: '',
    email: '',
    phone: '',
    clientBrandUrl: '',
    password: '',
    confirmPassword: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const suggestedSlug = useMemo(() => slugify(form.businessName), [form.businessName]);

  const setField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'businessName' && !prev.companySlug) {
        next.companySlug = slugify(value);
      }
      return next;
    });
  };

  const onLogoChange = (file) => {
    setLogoFile(file || null);
    if (!file) {
      setLogoPreview('');
      return;
    }
    const url = URL.createObjectURL(file);
    setLogoPreview(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const companySlug = slugify(form.companySlug || suggestedSlug);
    if (!companySlug) return setError('Please choose a company URL slug.');
    if (!form.businessName.trim()) return setError('Business name is required.');
    if (!form.ownerName.trim()) return setError('Owner / manager name is required.');
    if (!form.username.trim()) return setError('Username is required.');
    if (!form.email.trim()) return setError('Email is required.');
    if (!form.password) return setError('Password is required.');
    if (form.password.length < 6) return setError('Password must be at least 6 characters.');
    if (form.password !== form.confirmPassword) return setError('Passwords do not match.');

    try {
      setBusy(true);
      const payload = new FormData();
      payload.append('businessName', form.businessName);
      payload.append('companySlug', companySlug);
      payload.append('taxId', form.taxId);
      payload.append('address', form.address);
      payload.append('ownerName', form.ownerName);
      payload.append('username', form.username);
      payload.append('email', form.email);
      payload.append('phone', form.phone);
      payload.append('clientBrandUrl', form.clientBrandUrl);
      payload.append('password', form.password);
      if (logoFile) payload.append('clientLogo', logoFile);

      const response = await publicApi.post('/public/buzzersolo/signup', payload, {
        withCredentials: true,
      });

      const { data } = await axios.get(`${config.API_BASE_URL}/csrf-token`, { withCredentials: true });
      axios.defaults.headers.common['X-CSRF-Token'] = data.csrfToken;
      sessionStorage.setItem('csrfToken', data.csrfToken);

      if (response?.data?.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        if (typeof onLogin === 'function') {
          await onLogin(response.data.user);
        }
      }

      navigate('/solo/billing?onboarding=1', { replace: true });
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create your account');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bsolo-page">
      <div className="bsolo-shell" style={{ maxWidth: 980 }}>
        <div className="bsolo-brandbar">
          <BrandMark href="/" size={36} />
          <div className="bsolo-brandbar__actions">
            <Link className="bsolo-link-chip" to="/buzzerfeedback-solo">Back to Solo page</Link>
            <Link className="bsolo-link-chip" to="/login">I already have an account</Link>
          </div>
        </div>

        <section className="bsolo-card">
          <div className="bsolo-pill bsolo-pill--accent">Step 1 of 2 • Create your EstiaSync account</div>
          <h1 style={{ marginTop: 12 }}>Register for BuzzerFeedback Solo</h1>
          <p className="bsolo-muted bsolo-lead">
            Add the company details needed for invoicing now. You can also set an optional URL and logo
            for the customer view. If you skip the branding fields, Solo still works normally and simply hides them.
          </p>

          {error ? <div className="bsolo-card bsolo-alert" style={{ marginTop: 16 }}>{error}</div> : null}

          <form onSubmit={handleSubmit} className="bsolo-stack" style={{ marginTop: 20 }}>
            <div className="bsolo-grid cols-2">
              <div className="bsolo-form-section">
                <h3>Company & invoice details</h3>
                <div className="bsolo-grid cols-2">
                  <div className="bsolo-field">
                    <label>Business / company name</label>
                    <input value={form.businessName} onChange={(e) => setField('businessName', e.target.value)} placeholder="e.g. Casa Verde" />
                  </div>
                  <div className="bsolo-field">
                    <label>VAT / ΑΦΜ</label>
                    <input value={form.taxId} onChange={(e) => setField('taxId', e.target.value)} placeholder="Optional now, useful for invoices" />
                  </div>
                  <div className="bsolo-field" style={{ gridColumn: '1 / -1' }}>
                    <label>Billing address</label>
                    <input value={form.address} onChange={(e) => setField('address', e.target.value)} placeholder="Street, number, city" />
                  </div>
                  <div className="bsolo-field">
                    <label>Owner / manager name</label>
                    <input value={form.ownerName} onChange={(e) => setField('ownerName', e.target.value)} />
                  </div>
                  <div className="bsolo-field">
                    <label>Phone</label>
                    <input value={form.phone} onChange={(e) => setField('phone', e.target.value)} />
                  </div>
                  <div className="bsolo-field" style={{ gridColumn: '1 / -1' }}>
                    <label>Email</label>
                    <input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="bsolo-form-section">
                <h3>Login & client branding</h3>
                <div className="bsolo-grid cols-2">
                  <div className="bsolo-field">
                    <label>Company URL slug</label>
                    <input value={form.companySlug} onChange={(e) => setField('companySlug', e.target.value)} placeholder={suggestedSlug || 'casa-verde'} />
                    <div className="bsolo-meta">Used for login URL: /login/{slugify(form.companySlug || suggestedSlug) || 'your-company'}</div>
                  </div>
                  <div className="bsolo-field">
                    <label>Admin username</label>
                    <input value={form.username} onChange={(e) => setField('username', e.target.value)} autoCapitalize="none" autoCorrect="off" />
                  </div>
                  <div className="bsolo-field" style={{ gridColumn: '1 / -1' }}>
                    <label>Venue URL for customer view (optional)</label>
                    <input value={form.clientBrandUrl} onChange={(e) => setField('clientBrandUrl', e.target.value)} placeholder="https://yourvenue.gr" />
                    <div className="bsolo-meta">If you skip it, the customer page simply hides the website link.</div>
                  </div>
                  <div className="bsolo-field" style={{ gridColumn: '1 / -1' }}>
                    <label>Venue logo image (optional)</label>
                    <div className="bsolo-upload-box">
                      <input type="file" accept="image/*" onChange={(e) => onLogoChange(e.target.files?.[0] || null)} />
                      <div className="bsolo-meta" style={{ marginTop: 8 }}>PNG or JPG, up to about 2 MB.</div>
                      {logoPreview ? (
                        <div className="bsolo-row" style={{ alignItems: 'center', marginTop: 12 }}>
                          <img src={logoPreview} alt="Logo preview" className="bsolo-logo-preview" />
                          <button type="button" className="bsolo-btn-secondary" onClick={() => onLogoChange(null)}>Remove logo</button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="bsolo-field">
                    <label>Password</label>
                    <input type="password" value={form.password} onChange={(e) => setField('password', e.target.value)} />
                  </div>
                  <div className="bsolo-field">
                    <label>Confirm password</label>
                    <input type="password" value={form.confirmPassword} onChange={(e) => setField('confirmPassword', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>

            <div className="bsolo-actions" style={{ marginTop: 0 }}>
              <button type="submit" className="bsolo-btn" disabled={busy}>{busy ? 'Creating account...' : 'Continue to activation'}</button>
              <Link className="bsolo-btn-secondary" to="/buzzerfeedback-solo">Back</Link>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
